/**
 * 팝업 관리자 라이브 미리보기.
 *
 * 폼 값(현재 언어·구간)으로 mock 팝업을 미리보기 스테이지에 렌더한다(§13).
 * 스테이지 위에서 팝업을 드래그하면 현재 구간 위치가 place[bp][x_offset|y_offset] 에 반영되고
 * preset 은 custom·anchor 는 top-left 로 전환된다(§5). 화면 밖으로 완전히 벗어나지 않도록 클램프.
 *
 * 정밀 좌표 계약(프론트 렌더러와 동일):
 *  - preset ≠ custom : 9분할 preset + edge_margin 으로 배치
 *  - preset = custom : anchor 코너 기준 (x_offset, y_offset) px (기준 뷰포트 px)
 */

const REFERENCE_WIDTH: Record<string, number> = { desktop: 1280, tablet: 768, mobile: 375 };

function esc(s: string): string {
    return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] as string));
}

export class PopupPreview {
    private form: HTMLElement;
    private stage: HTMLElement | null;
    private canvas: HTMLElement | null;
    private device = "desktop";
    private locale: string;
    private dragging = false;

    constructor(form: HTMLElement) {
        this.form = form;
        this.stage = form.querySelector<HTMLElement>("[data-popup-preview-stage]");
        this.canvas = form.querySelector<HTMLElement>("[data-popup-preview-canvas]");
        const localeSel = form.querySelector<HTMLSelectElement>("[data-popup-preview-locale]");
        this.locale = localeSel?.value || this.firstLocale();
        this.bindDeviceButtons();
        this.bindLocaleSelect(localeSel);
    }

    private firstLocale(): string {
        const first = this.form.querySelector<HTMLElement>("[data-locale-panel]");
        return first?.getAttribute("data-locale-panel") || "ko";
    }

    private bindDeviceButtons(): void {
        this.form.querySelectorAll<HTMLButtonElement>("[data-popup-preview-device]").forEach((btn) => {
            btn.addEventListener("click", () => {
                this.device = btn.getAttribute("data-popup-preview-device") || "desktop";
                this.form.querySelectorAll("[data-popup-preview-device]").forEach((b) => b.classList.remove("is-active"));
                btn.classList.add("is-active");
                if (this.stage) this.stage.setAttribute("data-device", this.device);
                this.refresh();
            });
        });
    }

    private bindLocaleSelect(sel: HTMLSelectElement | null): void {
        sel?.addEventListener("change", () => {
            this.locale = sel.value;
            this.refresh();
        });
    }

    private val(name: string): string {
        const el = this.form.querySelector<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(`[name="${name}"]`);
        return el ? String(el.value ?? "") : "";
    }

    private placeVal(field: string): string {
        return this.val(`place[${this.device}][${field}]`);
    }

    /** 미리보기 재렌더. */
    refresh(): void {
        if (!this.canvas || !this.stage) return;
        if (this.dragging) return; // 드래그 중에는 재빌드하지 않음(포인터 유지)

        const template = this.form.getAttribute("data-popup-template") || "image-card";
        const title = this.val(`tr[${this.locale}][title]`);
        const subtitle = this.val(`tr[${this.locale}][subtitle]`);
        const desc = this.val(`tr[${this.locale}][description]`);
        const button = this.val(`tr[${this.locale}][button_text]`);
        const closeText = this.val(`tr[${this.locale}][close_text]`) || "✕";
        const todayText = this.val(`tr[${this.locale}][today_hide_text]`) || "오늘 하루 보지 않기";
        const hidePolicy = this.val("hide_policy");
        const showClose = this.readBool("close_button", true);
        const hasImage = template !== "text-notice";

        const parts: string[] = [];
        parts.push('<div class="pp-mock__inner">');
        if (showClose) parts.push(`<button type="button" class="pp-mock__close" tabindex="-1">${esc(closeText)}</button>`);
        if (hasImage) parts.push('<div class="pp-mock__image">이미지</div>');
        if (template !== "image-only") {
            parts.push('<div class="pp-mock__body">');
            if (subtitle) parts.push(`<p class="pp-mock__subtitle">${esc(subtitle)}</p>`);
            if (title) parts.push(`<p class="pp-mock__title">${esc(title)}</p>`);
            if (desc) parts.push(`<div class="pp-mock__desc">${esc(this.stripTags(desc))}</div>`);
            if (button) parts.push(`<span class="pp-mock__cta">${esc(button)}</span>`);
            parts.push("</div>");
        }
        if (hidePolicy && hidePolicy !== "none") {
            parts.push(`<label class="pp-mock__today"><input type="checkbox" tabindex="-1" disabled> ${esc(todayText)}</label>`);
        }
        parts.push("</div>");

        // 기존 mock 제거 후 재생성
        this.canvas.innerHTML = "";
        const mock = document.createElement("div");
        mock.className = `pp-mock pp-mock--${template}`;
        mock.setAttribute("data-popup-preview-mock", "");
        mock.innerHTML = parts.join("");

        // 크기(현재 구간 media width)
        const width = this.val(`media[${this.device}][width]`);
        if (width) mock.style.width = /^\d+$/.test(width) ? `${width}px` : width;

        this.canvas.appendChild(mock);
        this.applyPosition(mock);
        this.bindDrag(mock);

        if (!title && !subtitle && !desc && !hasImage) {
            this.canvas.innerHTML = '<p class="admin-popup-preview__placeholder">값을 입력하면 여기에 미리보기가 표시됩니다.</p>';
        }
    }

    /** placement 값 → 스테이지 내 절대 위치. */
    private applyPosition(mock: HTMLElement): void {
        if (!this.stage) return;
        const preset = this.placeVal("preset") || "center";
        const edge = parseInt(this.placeVal("edge_margin") || "20", 10) || 0;
        const scale = this.stage.clientWidth / (REFERENCE_WIDTH[this.device] || 1280);
        const em = Math.round(edge * scale);

        mock.style.position = "absolute";
        mock.style.left = "";
        mock.style.right = "";
        mock.style.top = "";
        mock.style.bottom = "";
        mock.style.transform = "";

        if (preset === "custom") {
            const x = Math.round((parseInt(this.placeVal("x_offset") || "0", 10) || 0) * scale);
            const y = Math.round((parseInt(this.placeVal("y_offset") || "0", 10) || 0) * scale);
            mock.style.left = `${x}px`;
            mock.style.top = `${y}px`;
            return;
        }

        const [vert, horiz] = this.presetParts(preset);
        if (horiz === "left") mock.style.left = `${em}px`;
        else if (horiz === "right") mock.style.right = `${em}px`;
        else { mock.style.left = "50%"; mock.style.setProperty("--pp-tx", "-50%"); }
        if (vert === "top") mock.style.top = `${em}px`;
        else if (vert === "bottom") mock.style.bottom = `${em}px`;
        else { mock.style.top = "50%"; mock.style.setProperty("--pp-ty", "-50%"); }

        const tx = horiz === "center" ? "-50%" : "0";
        const ty = vert === "middle" || vert === "center" ? "-50%" : "0";
        mock.style.transform = `translate(${tx}, ${ty})`;
    }

    private presetParts(preset: string): [string, string] {
        // top-left / middle-right / bottom-center / center …
        if (preset === "center") return ["middle", "center"];
        const [v, h] = preset.split("-");
        return [v || "middle", h || "center"];
    }

    /** 포인터 드래그로 위치 이동 → place[bp] 갱신(custom). */
    private bindDrag(mock: HTMLElement): void {
        mock.addEventListener("pointerdown", (ev: PointerEvent) => {
            if ((ev.target as HTMLElement).closest(".pp-mock__close, .pp-mock__cta")) return;
            if (!this.stage) return;
            ev.preventDefault();
            this.dragging = true;
            mock.setPointerCapture(ev.pointerId);
            mock.classList.add("is-dragging");

            const stageRect = this.stage.getBoundingClientRect();
            const mockRect = mock.getBoundingClientRect();
            const grabX = ev.clientX - mockRect.left;
            const grabY = ev.clientY - mockRect.top;

            const onMove = (e: PointerEvent): void => {
                let left = e.clientX - stageRect.left - grabX;
                let top = e.clientY - stageRect.top - grabY;
                // viewport 밖으로 완전히 벗어나지 않도록 클램프(§5)
                const maxLeft = stageRect.width - Math.min(mockRect.width, 40);
                const maxTop = stageRect.height - Math.min(mockRect.height, 40);
                left = Math.max(-(mockRect.width - 40), Math.min(left, maxLeft));
                top = Math.max(0, Math.min(top, maxTop));
                mock.style.left = `${left}px`;
                mock.style.top = `${top}px`;
                mock.style.right = "";
                mock.style.bottom = "";
                mock.style.transform = "";
            };
            const onUp = (e: PointerEvent): void => {
                mock.removeEventListener("pointermove", onMove);
                mock.removeEventListener("pointerup", onUp);
                mock.classList.remove("is-dragging");
                this.dragging = false;
                // 스테이지 좌표 → 기준 뷰포트 px 저장
                const scale = this.stage!.clientWidth / (REFERENCE_WIDTH[this.device] || 1280);
                const left = parseFloat(mock.style.left || "0");
                const top = parseFloat(mock.style.top || "0");
                this.setPlace("x_offset", Math.round(left / (scale || 1)));
                this.setPlace("y_offset", Math.round(top / (scale || 1)));
                this.setPlacePreset("custom");
            };
            mock.addEventListener("pointermove", onMove);
            mock.addEventListener("pointerup", onUp);
        });
    }

    private setPlace(field: string, value: number): void {
        const el = this.form.querySelector<HTMLInputElement>(`[name="place[${this.device}][${field}]"]`);
        if (el) { el.value = String(value); el.dispatchEvent(new Event("change", { bubbles: true })); }
    }

    private setPlacePreset(value: string): void {
        const el = this.form.querySelector<HTMLSelectElement>(`[name="place[${this.device}][preset]"]`);
        if (el) { el.value = value; el.dispatchEvent(new Event("change", { bubbles: true })); }
    }

    private readBool(name: string, dflt: boolean): boolean {
        const el = this.form.querySelector<HTMLInputElement>(`input[type="checkbox"][name="${name}"]`);
        return el ? el.checked : dflt;
    }

    private stripTags(html: string): string {
        const div = document.createElement("div");
        div.innerHTML = html;
        return (div.textContent || "").slice(0, 300);
    }
}
